import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BellIcon,
  GlobeAltIcon,
  ArrowRightOnRectangleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTour } from './TourProvider';
import i18n from '../i18n';

function NotificationDropdown({ onClose, user }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    if (user) {
      params.append('role', user.role);
      params.append('email', user.email);
    }

    axios.get(`/api/v1/reporting/notifications?${params.toString()}`)
      .then(res => setNotifications(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [user]);

  const markAllRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  return (
    <div className="absolute -right-12 sm:right-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 bg-white border border-gray-200 shadow-xl z-[200] animate-fade-in-down rounded-lg overflow-hidden">
      <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
        <span className="text-[10px] font-black uppercase tracking-widest text-gray-800">{i18n.t('notifs')}</span>
        <button onClick={markAllRead} className="text-[9px] font-bold text-[#155e75] hover:underline uppercase">{i18n.t('mark_read')}</button>
      </div>
      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="p-10 text-center text-[10px] text-gray-400 font-bold uppercase tracking-widest animate-pulse">{i18n.t('alert_sync')}</div>
        ) : notifications.length === 0 ? (
          <div className="p-10 text-center text-[10px] text-gray-400 font-bold uppercase tracking-widest">{i18n.t('no_alerts')}</div>
        ) : notifications.map(n => (
          <div key={n.id} className={`p-4 border-b border-gray-50 cursor-pointer hover:bg-blue-50/30 transition-colors ${!n.read ? 'bg-blue-50/10' : ''}`}>
            <div className="flex justify-between items-start mb-1 gap-2">
              <h4 className={`text-xs font-bold truncate ${!n.read ? 'text-[#155e75]' : 'text-gray-700'}`}>{n.title}</h4>
              <span className="text-[9px] text-gray-400 font-medium flex-shrink-0">{n.time}</span>
            </div>
            <p className="text-[10px] text-gray-500 line-clamp-2">{n.body}</p>
          </div>
        ))}
      </div>
      <div className="p-3 text-center bg-gray-50/50 border-t border-gray-100">
        <button className="text-[10px] font-black text-gray-400 uppercase tracking-widest hover:text-gray-600">{i18n.t('view_history')}</button>
      </div>
    </div>
  );
}

function LanguageDropdown({ currentLang, setLang }) {
  return (
    <div className="absolute right-0 mt-2 w-36 bg-white border border-gray-200 shadow-xl z-[200] animate-fade-in-down rounded-lg overflow-hidden">
      <div className="p-2 border-b border-gray-50 bg-gray-50">
        <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">{i18n.t('select_region')}</span>
      </div>
      <button onClick={() => setLang('EN')} className={`w-full text-left p-2.5 text-xs font-bold hover:bg-blue-50 transition-colors border-b border-gray-50 flex items-center justify-between ${currentLang === 'EN' ? 'text-[#155e75]' : 'text-gray-600'}`}>
        {i18n.t('lang_en')}
        {currentLang === 'EN' && <span className="w-1.5 h-1.5 rounded-full bg-[#155e75]" />}
      </button>
      <button onClick={() => setLang('FR')} className={`w-full text-left p-2.5 text-xs font-bold hover:bg-blue-50 transition-colors flex items-center justify-between ${currentLang === 'FR' ? 'text-[#155e75]' : 'text-gray-600'}`}>
        {i18n.t('lang_fr')}
        {currentLang === 'FR' && <span className="w-1.5 h-1.5 rounded-full bg-[#155e75]" />}
      </button>
    </div>
  );
}

function Header() {
  const { user, logout } = useAuth();
  const { startTour } = useTour();
  const navigate = useNavigate();
  const isAuth = !!user;

  const [notifOpen, setNotifOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [lang, setLang] = useState('EN');

  useEffect(() => {
    const saved = localStorage.getItem('lang') || 'en';
    setLang(saved.toUpperCase());
  }, []);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setNotifOpen(false);
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="h-16 bg-white border-b border-gray-200 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-[100] shadow-sm">
      <div className="flex items-center">
        <Link to="/" className="flex items-center gap-2 sm:gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#155e75] rounded-lg flex items-center justify-center shadow-md overflow-hidden relative group">
            <div className="absolute inset-0 bg-white/20 transform -skew-x-12 translate-x-10 group-hover:-translate-x-10 transition-transform duration-700" />
            <img src="/go-logo.svg" alt="MAP GO" className="w-6 h-6 sm:w-7 sm:h-7 relative z-10" />
          </div>
          <div className="flex flex-col">
            <span className="text-base sm:text-lg font-black text-[#155e75] tracking-tighter uppercase leading-none italic">
              MAP <span className="font-light text-gray-400 not-italic">GO</span>
            </span>
            <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest leading-none mt-1 hidden min-[400px]:block">
              {i18n.t('subtitle')}
            </span>
          </div>
        </Link>
      </div>

      <div className="flex items-center gap-2 sm:gap-4 md:gap-6" ref={dropdownRef}>
        <div className="flex items-center gap-1.5 sm:gap-3 pr-2 sm:pr-4 border-r border-gray-100 h-10">
          <button
            onClick={startTour}
            className="p-1.5 sm:p-2 rounded-lg bg-gray-50 text-gray-400 hover:text-[#155e75] hover:bg-slate-100 transition-all"
            title={i18n.t('help_hub')}
          >
            <QuestionMarkCircleIcon className="w-5 h-5" />
          </button>

          <div className="relative">
            <button
              onClick={() => { setLangOpen(!langOpen); setNotifOpen(false); }}
              className={`p-1.5 sm:p-2 rounded-lg transition-all flex items-center gap-1 ${langOpen ? 'bg-blue-50 text-[#155e75]' : 'bg-gray-50 text-gray-400 hover:text-gray-600'}`}
            >
              <GlobeAltIcon className="w-5 h-5" />
              <span className="text-[10px] font-bold uppercase hidden xs:inline">{lang}</span>
            </button>
            {langOpen && (
              <LanguageDropdown
                currentLang={lang}
                setLang={(l) => {
                  i18n.setLang(l);
                  window.location.reload();
                }}
              />
            )}
          </div>

          {isAuth && (
            <div className="relative">
              <button
                onClick={() => { setNotifOpen(!notifOpen); setLangOpen(false); }}
                className={`p-1.5 sm:p-2 rounded-lg transition-all relative ${notifOpen ? 'bg-blue-50 text-[#155e75]' : 'bg-gray-50 text-gray-400 hover:text-gray-600'}`}
              >
                <BellIcon className="w-5 h-5" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white" />
              </button>
              {notifOpen && <NotificationDropdown user={user} onClose={() => setNotifOpen(false)} />}
            </div>
          )}
        </div>

        {isAuth ? (
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-[10px] font-black text-gray-700 leading-tight tracking-tight uppercase max-w-[120px] truncate">
                {user.email.split('@')[0]}
              </p>
              <p className="text-[9px] text-[#155e75] font-black uppercase tracking-widest">
                {user.role}
              </p>
            </div>
            <div className="w-8 h-8 rounded-full bg-[#155e75]/10 flex items-center justify-center text-[#155e75] font-black text-xs border border-[#155e75]/20 flex-shrink-0">
              {user.email[0].toUpperCase()}
            </div>
            <button
              onClick={logout}
              className="p-1.5 sm:p-2 rounded-lg bg-gray-50 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all border border-gray-100 flex-shrink-0"
              title="Sign Out"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <Link
            to="/login"
            className="btn-grexel px-4 sm:px-6 py-1.5 sm:py-2 text-xs sm:text-sm shadow-sm font-bold rounded-lg transition-all"
          >
            Login
          </Link>
        )}
      </div>
    </header>
  );
}

export default Header;